/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

// tslint:disable:variable-name

import { ID } from '@gh/common-shared-data';
import { createEnum, EnumSet, WithOptimisticLock, WithPrivilegedToken } from '@gh/core-data';
import { CommunicationAttachmentDto } from '../communications';
import { Month } from '../offices/models/office-details.dto';

export const WipEntryPoint = createEnum('WIP_ENTRY_POINT', {
  Produce: 'PRODUCE',
  View: 'VIEW',
});

export type WipEntryPoint = EnumSet<typeof WipEntryPoint>;

export type AccountingWipMonthDto = {
  month: Month;
  year: number;
};

export type AccountingWipXeroAccountDto = {
  externalId: ID;
  name: string;
  code: string;
};

export type AccountingWipInitDto = {
  months: AccountingWipMonthDto[];
  isCurrentFinancialYear: boolean;
  wipAssetAccount: AccountingWipXeroAccountDto;
  wipOpening: AccountingWipXeroAccountDto;
  wipClosing: AccountingWipXeroAccountDto;
  endOfTheYear: boolean;
};

export type AccountingWipApplyRequestDto = {
  year: number;
  month: Month;
  showDpp: boolean;
};

export type AccountingWipApplyResponseDto = {
  entryPoint: WipEntryPoint;
  journal: AccountingWipJobDto[];
  endOfTheYear: boolean;
};

export type AccountingWipRecalculateRequestDto = WithPrivilegedToken<{
  year: number;
  month: Month;
  isPersist: boolean;
  journal: AccountingWipJobDto[];
}>;

export type AccountingWipRecalculateResponseDto = {
  journal: AccountingWipJobDto[];
};

export type AccountingWipCreateAndPrintRequestDto = {
  authCode?: string;
  printerId?: ID;
  journal: AccountingWipJobDto[];
  totals: AccountingWipTotalDto;
};

export type AccountingWipJobDto = WithPrivilegedToken<{
  wpaId: ID;
  jobId: ID;
  jobNumber: string;
  customerName: string;
  totalPrice: number;
  drawnToDate: number;
  drawnToDatePct: number;
  budgetedCost: number;
  invoicesToDate: number;
  invoicesToDatePct: number;
  budgetedProfit: number;
  underruns: number;
  overruns: number;
  overrunsAdjust?: number;
  wip: number;
  wipAasb111: number;
  isExcludeDpp: boolean;
}>;

export type AccountingWipJournalLogItemDto = {
  wjlId: ID;
  entryDate: Date;
  accountingDate: Date;
  accountCode: string;
  accountName: string;
  debitAmount?: number;
  creditAmount?: number;
  groupId: ID;
  groupSeq: number;
  externalId: ID;
  xeroUrl: string;
  reversed: boolean;
};

export type AccountingWipPrintRequestDto = {
  authCode: string;
  printerId: ID;
  report: CommunicationAttachmentDto;
};

export type AccountingWipReportRequestDto = {
  year: number;
  month: Month;
};

export type AccountingWipCreateAndPrintReportDto = {
  fileName: string;
  webViewLink: string;
};

export type AccountingWipTotalDto = {
  totalPrices: number;
  totalBudgetCost: number;
  totalInvoices: number;
  totalBudgetProfit: number;
  totalOver: number;
  totalUnder: number;
  totalOverAdj?: number;
  totalWip: number;
  totalAasb: number;
  totalClaimed: number;
};
